<template>
    <section class="section-details">
        <header>{{ $t("chatbotv2.chatDetail") }}</header>
        <div class="archives-details">
            <section class="user" v-if="!!conversation">
                <div class="avatar">
                    <Avatar
                        :size="20"
                        :username="$_.get(conversation, 'fullname', '')"
                        class="sc-message--avatar"
                        style="color: white; font-size: 15px"
                    />
                </div>
                <div class="visitorName">{{ $_.get(conversation, "fullname") }}</div>
            </section>
            <section class="attributes" v-if="!!conversation">
                <div class="archives-attribute">
                    <span class="material-icons"> schedule </span>
                    <div text-truncate="" text-type="h6" text-weight="semi-bold">
                        <span text-color="default" aria-expanded="false"> {{ startDateHumanized() }} </span>
                    </div>
                </div>
                <span>
                    <button class="userProfile" @click="() => seeVisitorDetail()">
                        {{ $t("chatbotv2.allUserDetail") }}
                    </button>
                </span>
            </section>
            <section class="hr" v-if="!!conversation" />
            <section class="attributes" v-if="!!conversation">
                <div text-color="default" text-weight="semi-bold" text-type="h5">{{ $t("chatbotv2.sessionData") }}</div>
                <div class="archives-attribute">
                    <span class="material-icons">schema</span>
                    <div
                        class="attribute-name"
                        v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                        :content="$t('chatbotv2.botStory')"
                    >
                        {{ $t("chatbotv2.botStory") }}
                    </div>
                    <div text-truncate="" text-type="h6" text-weight="semi-bold" class="attribute-value link">
                        <a :href="getStoryLink()" class="" text-color="link" aria-expanded="false" @click="gotoStory">
                            {{ getStoryName() }}
                        </a>
                    </div>
                </div>
                <div class="archives-attribute">
                    <span class="material-icons"> public </span>
                    <div
                        class="attribute-name"
                        v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                        :content="$t('chatbotv2.integration')"
                    >
                        {{ $t("chatbotv2.integration") }}
                    </div>
                    <div class="attribute-value">
                        <span
                            text-color="default"
                            aria-expanded="false"
                            v-if="getAttributeValue('integration') === 'web'"
                            >{{ $t("chatbotv2.supsisChatWidget") }}
                        </span>
                        <span
                            text-color="default"
                            aria-expanded="false"
                            v-else-if="getAttributeValue('integration') === 'whatsapp'"
                        >
                            Whatsapp
                        </span>
                    </div>
                </div>
                <div class="archives-attribute" v-for="attr of getAttributes()">
                    <AttributeIconPreview :attribute="attr.name" />
                    <div
                        class="attribute-name"
                        v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                        :content="getAttributeName(attr.name)"
                    >
                        {{ getAttributeName(attr.name) }}
                    </div>
                    <div
                        class="attribute-value"
                        v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                        :content="attr.value"
                    >
                        <span text-color="default" aria-expanded="false"> {{ attr.value }}</span>
                    </div>
                </div>
            </section>
        </div>
    </section>
</template>

<script>
import log2 from "../../../../../utils/log2";
import Avatar from "vue-avatar";
import moment from "moment";
import { mapState, mapGetters, mapActions } from "vuex";
import _ from "lodash";
import AttributeIconPreview from "../dashboard/common/AttributeIconPreview";
import CONFIG from "../../../../../config/config";
import systemAttributes from "../../utils/systemAttributes";
import { PERMISSION_TYPES } from "../../../../../language/permissionTypes";

const log = log2("ConversationDetail");
export default {
    name: "ConversationDetail",
    components: {
        Avatar,
        AttributeIconPreview,
    },
    props: {
        loading: {
            type: Boolean,
            required: false,
            default: false,
        },
        conversation: {
            type: Object,
            required: false,
        },
    },
    computed: {
        ...mapState({
            stories: (state) => state.CB.stories,
        }),
        ...mapGetters({
            checkPermission: "auth/checkPermission",
        }),
        getConversation() {
            return this.conversation;
        },
        visitorDetailPermission() {
            return [
                PERMISSION_TYPES["visitor.delete"].tr.name,
                PERMISSION_TYPES["visitor.update"].tr.name,
                PERMISSION_TYPES["visitor.block"].tr.name,
                PERMISSION_TYPES["visitor.unblock"].tr.name,
            ].some((permission) => this.checkPermission(permission));
        },
    },
    methods: {
        ...mapActions({
            addNewWindow: "app/addNewWindow",
        }),
        seeVisitorDetail() {
            if (!this.visitorDetailPermission) {
                this.$message.error(this.$t("permissionErrors.visitorDetailRead"));
            } else {
                this.addNewWindow({
                    type: "show-visitor",
                    title: `${_.get(this.conversation, "fullname", "") || "Visitor"} Profile`,
                    payload: {
                        visitorId: _.get(this.conversation, "visitor._id", ""),
                    },
                    footer: false,
                });
            }
        },
        getAttributeName(attribute) {
            log("getAttributeName", this.$t(`chatbotv2.${attribute}`));
            if (attribute.startsWith("akinon_")) {
                const foundAttr = CONFIG.AKINON.attributes.find((attr) => attr.value === attribute);
                return _.get(foundAttr, "name", attribute);
            } else if (attribute.startsWith("probel_")) {
                const foundAttr = CONFIG.PROBEL.attributes.find((attr) => attr.value === attribute);
                return _.get(foundAttr, "name", attribute);
            } else if (attribute.startsWith("ideasoft_")) {
                const foundAttr = CONFIG.IDEASOFT.attributes.find((attr) => attr.value === attribute);
                return _.get(foundAttr, "name", attribute);
            } else {
                const foundAttr = systemAttributes.find((x) => x.value === attribute);
                return _.get(foundAttr, "name", attribute);
            }
        },
        getAttributes() {
            const attributesObject = _.get(this.conversation, "chatbotState.attributes", {});
            const attributes = Object.keys(attributesObject)
                .map((key) => {
                    return {
                        name: key,
                        value: attributesObject[key],
                    };
                })
                .filter((attribute) => !_.isEmpty(attribute.value));
            return attributes;
        },
        getAttributeValue(attributeName, defaultValue) {
            const foundAttr = this.getAttributes().find((x) => x.name === attributeName);
            return _.get(foundAttr, "value", defaultValue);
        },
        startDateHumanized() {
            const createdAt = _.get(this.conversation, "createdDate");
            return createdAt ? moment(createdAt, "YYYY-MM-DD HH:mm:ss").format("lll") : "";
        },
        getStoryName() {
            const storyId = _.get(this.conversation, "chatbotState.storyId");
            const foundStory = this.stories.find((x) => x._id === storyId);
            log("stori", this.stories);
            return _.get(foundStory, "title", `${this.$t("chatbotv2.goToStory")}`);
        },
        getStoryLink() {
            const storyId = _.get(this.conversation, "chatbotState.storyId");
            const storyVersion = _.get(this.conversation, "chatbotState.storyVersion");
            const foundStory = this.stories.find((x) => x._id === storyId);
            if (foundStory) {
                return `/chatbot/story/${foundStory._id}/${storyVersion}`;
            } else {
                return "#";
            }
        },
        gotoStory(e) {
            e.preventDefault();
            this.$router.push(this.getStoryLink());
        },
    },
};
</script>

<style scoped></style>
